import { Signal } from "@/constants/signal/signal"
import type { ReviewWithSignalsAndCompanyId } from "@/db/queries/parsed-review"
import {
  calculateWeightedRating,
  DEFAULT_MINIMUM_REVIEWS,
} from "@/helpers/company/review-utils"
import type { MethodologyOutput } from "@/types/signal-types"

export type CustomerGroupData = {
  name: string
  label: string
  sublabel: string
  rating: number
  displayRating: string
  hasInsufficientData: boolean
}

export type InsightPart = { text: string; shouldHighlight?: boolean }

export type LongTermInsight = {
  parts: InsightPart[]
  sentiment: "positive" | "neutral" | "negative"
}

// Distribution of long-term satisfaction deltas across 782 companies (snapshot 2026-03).
// Each entry maps a delta (longTerm - recent rating) to the percentile of companies
// at or below that delta.
const DELTA_PERCENTILE_TABLE: [delta: number, percentile: number][] = [
  [-1.0, 25],
  [-0.5, 43],
  [-0.2, 60],
  [0, 76],
  [0.2, 96],
  [0.5, 99],
  [1.0, 100],
]

/**
 * Returns the percentile rank (0–100) for a given delta by linearly
 * interpolating through the distribution table. Clamps at boundaries.
 */
export function getDeltaPercentile(delta: number): number {
  const table = DELTA_PERCENTILE_TABLE

  // Clamp below
  if (delta <= table[0][0]) return table[0][1]
  // Clamp above
  if (delta >= table[table.length - 1][0]) return table[table.length - 1][1]

  // Find the two adjacent points to interpolate between
  for (let i = 0; i < table.length - 1; i++) {
    const [d0, p0] = table[i]
    const [d1, p1] = table[i + 1]
    if (delta >= d0 && delta <= d1) {
      const t = (delta - d0) / (d1 - d0)
      return Math.round(p0 + t * (p1 - p0))
    }
  }

  return table[table.length - 1][1]
}

export function getLongTermInsight(
  data: CustomerGroupData[],
  companyName: string
): LongTermInsight | null {
  const recent = data.find((d) => d.name === "recent")
  const longTerm = data.find((d) => d.name === "longTerm")
  if (!recent || !longTerm) return null

  if (recent.hasInsufficientData && longTerm.hasInsufficientData) return null

  if (recent.hasInsufficientData) {
    return {
      parts: [
        {
          text: `Long-term customers rate ${companyName} ${longTerm.displayRating} ★`,
        },
      ],
      sentiment: "neutral",
    }
  }

  if (longTerm.hasInsufficientData) {
    return {
      parts: [
        {
          text: `Recent customers rate ${companyName} ${recent.displayRating} ★`,
        },
      ],
      sentiment: "neutral",
    }
  }

  const delta = longTerm.rating - recent.rating
  const percentile = getDeltaPercentile(delta)

  if (delta >= 0.2) {
    return {
      parts: [
        {
          text: `Long-term customers rate ${companyName} ${longTerm.displayRating} ★ — higher than early reviews. This growth is better than ${percentile}% of installers we looked at.`,
        },
      ],
      sentiment: "neutral",
    }
  }

  if (delta > -0.2) {
    return {
      parts: [
        {
          text: `Long-term satisfaction for ${companyName} holds steady at ${longTerm.displayRating} ★. This is better than ${percentile}% of installers we looked at.`,
        },
      ],
      sentiment: "neutral",
    }
  }

  // delta <= -0.2
  if (percentile >= 40) {
    return {
      parts: [
        { text: `Long-term satisfaction for ${companyName} ` },
        { text: `drops to ${longTerm.displayRating} ★`, shouldHighlight: true },
        {
          text: ` compared to early reviews. This is better than ${percentile}% of installers we looked at.`,
        },
      ],
      sentiment: "negative",
    }
  }

  return {
    parts: [
      { text: `Long-term satisfaction for ${companyName} ` },
      { text: `drops to ${longTerm.displayRating} ★`, shouldHighlight: true },
      {
        text: ` compared to early reviews. This decline is worse than ${100 - percentile}% of installers we looked at.`,
      },
    ],
    sentiment: "negative",
  }
}

export function getCustomerGroupData(
  validReviews: ReviewWithSignalsAndCompanyId[],
  methodologyOutput: MethodologyOutput
): CustomerGroupData[] | null {
  // Filter to verified customers only (status = yes)
  const verifiedCustomers = validReviews.filter(
    (r) => r.signals?.[Signal.STATUS]?.label === "yes"
  )

  // If no verified customers at all, return null to hide section
  if (verifiedCustomers.length === 0) return null

  // Split into long-term (tenure = yes) and recent (tenure != yes)
  const longTermReviews = verifiedCustomers.filter(
    (r) => r.signals?.[Signal.TENURE]?.label === "yes"
  )
  const recentReviews = verifiedCustomers.filter(
    (r) => r.signals?.[Signal.TENURE]?.label !== "yes"
  )

  const recentRating = calculateWeightedRating(
    recentReviews,
    methodologyOutput,
    DEFAULT_MINIMUM_REVIEWS
  )
  const longTermRating = calculateWeightedRating(
    longTermReviews,
    methodologyOutput,
    DEFAULT_MINIMUM_REVIEWS
  )

  // Hide section if both groups have insufficient data
  if (recentRating === null && longTermRating === null) return null

  // For insufficient data bars, use the other group's rating to match height
  const fallbackRating = recentRating ?? longTermRating ?? 4.0

  return [
    {
      name: "recent",
      label: "RECENT CUSTOMERS",
      sublabel: "Less than 1 year",
      rating: recentRating ?? fallbackRating,
      displayRating: recentRating?.toFixed(1) ?? "Not enough data",
      hasInsufficientData: recentRating === null,
    },
    {
      name: "longTerm",
      label: "LONG-TERM CUSTOMERS",
      sublabel: "More than 1 year",
      rating: longTermRating ?? fallbackRating,
      displayRating: longTermRating?.toFixed(1) ?? "Not enough data",
      hasInsufficientData: longTermRating === null,
    },
  ]
}
